import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useReservoirAcceptBidPrecheck } from "@/hooks/reservoir/use-accept-bid-precheck";
import { useReservoirBuyPrecheck } from "@/hooks/reservoir/use-buy-precheck";
import { useConnectUser } from "@/hooks/use-connect-user";
import { CheckCircle, ShoppingCart, Signpost } from "lucide-react";
import type { Asset, OrderType } from "./order-dialog-v2";

const text = {
    buy: {
        left: {
            title: "Buy",
            description: "Buy a share at the currently available price.",
            outputType: "buy",
            icon: <ShoppingCart />,
        },
        right: {
            title: "Create Bid",
            description: "Create custom bid at the price you chose.",
            outputType: "create-bid",
            icon: <Signpost />,
        },
    },
    sell: {
        left: {
            title: "Accept Bid",
            description: "Accept bids from other users at this price.",
            outputType: "accept-bid",
            icon: <CheckCircle />,
        },
        right: {
            title: "Create Listing",
            description: "Create a custom listing at the price you chose.",
            outputType: "create-listing",
            icon: <Signpost />,
        },
    },
} as const;

export function BuySelect({
    asset,
    onOrderTypeChange,
}: {
    asset?: Asset;
    onOrderTypeChange: (orderType: OrderType) => void;
}) {
    const { cheapestPriceOnePercet } = useReservoirBuyPrecheck(asset?.blockchainTokenId ?? undefined);

    return (
        <div className="flex h-full flex-col justify-center py-16">
            <div className="flex w-full gap-4">
                <Card
                    onOrderTypeChange={onOrderTypeChange}
                    {...text.buy.left}
                    badge={<PriceBadge priceOnePercent={cheapestPriceOnePercet} />}
                    disabled={cheapestPriceOnePercet == null}
                />
                <Card onOrderTypeChange={onOrderTypeChange} {...text.buy.right} />
            </div>
        </div>
    );
}

export function SellSelect({
    asset,
    onOrderTypeChange,
}: {
    asset?: Asset;
    onOrderTypeChange: (orderType: OrderType) => void;
}) {
    const { mostExpensivePriceOnePercent } = useReservoirAcceptBidPrecheck(asset?.blockchainTokenId ?? undefined);

    return (
        <div className="flex h-full flex-col justify-center py-16">
            <div className="flex w-full gap-4">
                <Card
                    onOrderTypeChange={onOrderTypeChange}
                    {...text.sell.left}
                    badge={<PriceBadge priceOnePercent={mostExpensivePriceOnePercent} />}
                    disabled={mostExpensivePriceOnePercent == null}
                />
                <Card onOrderTypeChange={onOrderTypeChange} {...text.sell.right} />
            </div>
        </div>
    );
}

const PriceBadge = ({ priceOnePercent }: { priceOnePercent: string | undefined | null }) => {
    return (
        <div className="mt-2 flex flex-col items-center gap-1 md:flex-row md:gap-2">
            {priceOnePercent !== undefined ? (
                <Badge className="grayscale group-disabled:opacity-50">{priceOnePercent ?? "Not available"}</Badge>
            ) : (
                <Skeleton className="h-5 w-12 rounded-full bg-muted-foreground/50" />
            )}
            {priceOnePercent !== null && <span className="text-muted-foreground text-xs">Price for 1%</span>}
        </div>
    );
};

function Card({
    title,
    description,
    icon,
    outputType,
    onOrderTypeChange,
    badge,
    disabled,
}: {
    title: string;
    description: string;
    icon: React.ReactNode;
    outputType: OrderType;
    onOrderTypeChange: (orderType: OrderType) => void;
    badge?: React.ReactNode;
    disabled?: boolean;
}) {
    const { connectWithWallet } = useConnectUser();

    return (
        <button
            type="button"
            onClick={() => {
                if (connectWithWallet() === "already-connected") {
                    onOrderTypeChange(outputType);
                }
            }}
            disabled={disabled}
            className="group flex basis-1/2 flex-col items-center gap-4 rounded-md border bg-card p-4 text-left text-card-foreground shadow-xs hover:bg-muted focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground md:flex-row"
        >
            <div className="w-8">{icon}</div>

            <div className="flex flex-col items-center md:items-start">
                <div className="text-center font-semibold text-sm md:text-start">{title}</div>
                <div className="text-center text-muted-foreground text-sm md:text-start">{description}</div>
                {badge}
            </div>
        </button>
    );
}
